import { useQuery } from "@tanstack/react-query";
import apiClient from "@/modules/request.module.v2";

const isItemCompleted = (item) => {
  return item.completed || item.skippedAt;
};

const useUserProfileCompletion = (queryOptions = {}) => {
  const queryResult = useQuery({
    queryKey: ["userProfileCompletion"],
    staleTime: 1000 * 60 * 20, // 20 minutes,
    queryFn: async () => {
      return await apiClient.get(`users/profile-completion`);
    },
    ...queryOptions,
  });

  const completionData = queryResult?.data?.data;

  const totalWeight = completionData?.reduce(
    (acc, item) => acc + item.weight,
    0,
  );

  const sortedCompletionData = completionData?.toSorted((a, b) => {
    const completedDiff = isItemCompleted(a) - isItemCompleted(b);
    if (completedDiff !== 0) return completedDiff;
    return b.shouldRemind - a.shouldRemind;
  });

  const formattedCompletionData =
    sortedCompletionData?.map((item, idx) => ({
      ...item,
      weightPercentage:
        item.weight && totalWeight
          ? Math.round((item.weight / totalWeight) * 100)
          : 0,
      isCompleted: isItemCompleted(item),
      nextStageId:
        sortedCompletionData?.[idx + 1]?.id ?? sortedCompletionData?.[0]?.id,
      prevStageId:
        sortedCompletionData?.[idx - 1]?.id ??
        sortedCompletionData?.[sortedCompletionData.length - 1]?.id,
    })) ?? [];

  const stagesToBeCompleted =
    formattedCompletionData?.filter((i) => !isItemCompleted(i)) || [];

  const completedWeight =
    formattedCompletionData?.reduce(
      (acc, item) => (isItemCompleted(item) ? acc + item.weight : acc),
      0,
    ) || 0;

  const completionPercentage =
    totalWeight > 0 ? Math.round((completedWeight / totalWeight) * 100) : 0;

  const stagesById = formattedCompletionData.reduce(
    (acc, item) => ({ ...acc, [item.id]: item }),
    {},
  );

  return {
    ...queryResult,
    data: formattedCompletionData,
    completionPercentage,
    stagesToBeCompleted,
    stagesById,
  };
};

export { useUserProfileCompletion };
